import React from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { ProductOperator, ProductPeripheralsState, useProduct } from 'zens-store'
import { Button, Color, Spacing, Text } from 'zens-storybook'
import { InfoTable, InfoBlock, LoggingStatus, LoggingSync } from '@dms/component'
import { ProductConnectivity } from 'zens-fragment'

import ProductWorkspace from './workspace'
import ProductFloor from './floor'

const styles = StyleSheet.create({
  container: {},
  block: {
    marginBottom: Spacing.M,
  },
  blockContent: {
    alignSelf: 'flex-start',
  },
  error: {
    marginTop: Spacing.S,
    color: Color.darkRed,
  },
  logging: {
    marginTop: Spacing.S,
  },
  location: {
    flexDirection: 'row',
  },
  floor: {
    marginTop: 0,
  },
})

interface Props extends ViewProps {
  companyId: number
  productId: number
  onUninstall?: () => void
}
const ProductInfo = (props: Props) => {
  const { companyId, productId, style, onUninstall, ...otherProps } = props
  const product = useProduct({ companyId, productId })
  const { t } = useTranslation()
  const {
    connectivityState: connectivityStatus,
    peripheralsState,
    serialNumber,
    typeName,
    workspaceId = 0,
    operator,
    sim,
    imei,
    band,
    beacon,
    secondBeaconEnabled,
    secondBeacon,
    sensorModel,
  } = product.data || {}

  function infoForStatus() {
    if (peripheralsState === ProductPeripheralsState.LedDisconnected)
      return t('Ledring is missing or not installed correctly')
    else if (peripheralsState === ProductPeripheralsState.SensorDisconnected)
      return t('Sensor is missing or not installed correctly')
    else if (peripheralsState === ProductPeripheralsState.SensorAndLedDisconnected)
      return t('Sensor and ledring is missing or not installed correctly')
  }

  const configLocation = {
    fields: [
      {
        label: t('Located at'),
        value: product.data?.workspaceId ? (
          <View style={styles.location}>
            <ProductWorkspace companyId={companyId} product={product.data} />
            <Text>, </Text>
            <ProductFloor companyId={companyId} workspaceId={workspaceId} style={styles.floor} />
          </View>
        ) : (
          <Text>{t('Not installed')}</Text>
        ),
      },
    ],
  }

  const configDetail = {
    columns: [
      {
        groups: [
          {
            label: t('Product'),
            fields: [
              {
                label: t('Serial number'),
                value: serialNumber,
              },
              {
                label: t('Model'),
                value: typeName,
              },
            ],
          },
          {
            label: t('Network'),
            fields: [
              {
                label: t('Provider'),
                value: operator ? ProductOperator[operator] : undefined,
              },
              {
                label: t('SIM'),
                value: sim,
              },
              {
                label: t('IMEI'),
                value: imei,
              },
              {
                label: t('Band'),
                value: band,
              },
            ],
          },
        ],
      },
      {
        groups: [
          {
            label: t('Beacon'),
            fields: [
              {
                label: t('UUID'),
                value: beacon?.uuid,
              },
              {
                label: t('Major'),
                value: beacon?.major,
              },
              {
                label: t('Minor'),
                value: beacon?.minor,
              },
            ],
          },
          ...(secondBeaconEnabled
            ? [
                {
                  label: t('Second beacon'),
                  fields: [
                    {
                      label: t('UUID'),
                      value: secondBeacon?.uuid,
                    },
                    {
                      label: t('Major'),
                      value: secondBeacon?.major,
                    },
                    {
                      label: t('Minor'),
                      value: secondBeacon?.minor,
                    },
                  ],
                },
              ]
            : []),
          {
            label: t('Sensor'),
            fields: [
              {
                label: t('Occupancy'),
                value: sensorModel,
              },
            ],
          },
        ],
      },
    ],
  }

  const productStatus = <ProductConnectivity style={styles.blockContent} value={connectivityStatus} />
  const info = infoForStatus()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock title={{ text: t('Device status'), left: productStatus }} style={styles.block}>
        {info && (
          <Text style={styles.error} level={5}>
            {info}
          </Text>
        )}
      </InfoBlock>
      <InfoTable
        title={t('Device location')}
        companyId={companyId}
        style={styles.block}
        config={configLocation}
        buttonHref={!workspaceId ? `/companies/${companyId}/products/${productId}/transfer` : undefined}
        buttonPress={workspaceId ? onUninstall : undefined}
        buttonTitle={workspaceId ? t('Uninstall device') : t('Transfer device')}
        buttonIcon={workspaceId ? 'trash' : 'transfer'}
      />
      <InfoTable
        title={t('Device details')}
        companyId={companyId}
        style={styles.block}
        config={configDetail}
        buttonHref={`/companies/${companyId}/products/${productId}/edit`}
        unfolded
      />
      <InfoBlock
        title={{
          text: t('Sync status'),
          left: <LoggingStatus style={styles.blockContent} syncFailed={product.data?.syncFailed} />,
        }}
        style={styles.block}
        unfolded
      >
        <LoggingSync product={product.data} style={styles.logging} />
      </InfoBlock>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <InfoBlock.Skeleton style={styles.block} title={t('Device status')} />
      <InfoBlock.Skeleton style={styles.block} title={t('Device location')} />
      <InfoBlock.Skeleton style={styles.block} title={t('Device details')} right={<Button.Skeleton />} />
      <InfoBlock.Skeleton style={styles.block} title={t('Sync status')} />
    </View>
  )
}
ProductInfo.Skeleton = Skeleton

export default observer(ProductInfo)
